import React from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useState, useCallback } from 'react'
import { useMediaQuery } from '@mantine/hooks'

export interface ILearningSupportItem {
  title: string
  value: string
  description: string
}

export interface ILearningSupportPair {
  left: ILearningSupportItem
  right: ILearningSupportItem
}

interface Props {
  contents: ILearningSupportPair
  idx: number
}

export default function LearningSupportComparison({ contents, idx }: Props) {
  const isMobile = useMediaQuery('(max-width: 1023px)')
  const defaultOpen = idx === 0
  const [isOpen, setIsOpen] = useState(defaultOpen)

  const onOpenHandler = useCallback(() => {
    setIsOpen(prev => !prev)
  }, [])

  const { left, right } = contents
  const pairKey = `${left.title}-${left.value}`

  return (
    <div key={pairKey} className="flex flex-row items-stretch">
      {[left, right].map(item => {
        const isRight = item === right
        return (
          <div
            key={`${item.title}-${item.value}`}
            className={`h-auto flex-1 px-3 pb-2 lg:px-[42px] ${isRight ? 'bg-[#FFEDD5]' : 'bg-strong'}`}
          >
            <div
              className={`flex h-full w-full flex-col rounded p-3 lg:px-10 lg:py-6 ${
                isOpen
                  ? 'bg-white'
                  : item === right
                    ? 'bg-[#FFF7ED]' // 오른쪽 닫힘일 때
                    : 'bg-[#FAFAFA]' // 왼쪽 닫힘일 때
              }`}
            >
              <div
                className={`flex flex-row items-start lg:items-center ${isRight ? 'cursor-pointer' : ''} ${isMobile ? '' : 'group'}`}
                onClick={isRight ? onOpenHandler : undefined}
              >
                <dl className={`${isOpen ? 'gap-1' : 'gap-2.5'} flex flex-1 flex-col`}>
                  <dt className="text-xs font-bold text-label-alternative lg:text-sm">
                    {item.title}
                  </dt>
                  <dd
                    className={`min-h-[42px] whitespace-pre-line text-sm font-bold leading-normal lg:min-h-0 lg:whitespace-normal lg:text-lg ${
                      isRight ? 'text-yellow-450' : 'text-label-neutral'
                    }`}
                  >
                    {item.value}
                  </dd>
                </dl>

                {isRight && (
                  <div
                    className={`rounded p-1 transition-colors ${isMobile ? '' : 'group-hover:bg-yellow-450'} ${isOpen ? 'bg-alternative' : 'bg-white'}`}
                  >
                    <ChevronDownIcon
                      className={`${
                        isOpen ? 'rotate-180 transform' : ''
                      } ml-auto h-auto w-[13.3px] shrink-0 grow-0 text-label-normal lg:w-4 ${isMobile ? '' : 'group-hover:text-white'}`}
                    />
                  </div>
                )}
              </div>

              {isOpen && (
                <p
                  className={`mt-2 border-t pt-2 text-xs leading-normal lg:mt-3 lg:whitespace-pre-line lg:pt-3 lg:text-base ${
                    isRight ? 'text-label-normal' : 'text-label-neutral'
                  }`}
                >
                  {item.description}
                </p>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
